<template>
  <div class="note-history container-card">
    <div class="title">
      <h4>Historial de cambios</h4>
      <button class="nh-refresh" @click="fetchHistory" :disabled="loading" title="Actualizar">
        &#x21bb;
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="nh-state">
      <div class="nh-spinner"></div>
      <span>Cargando historial…</span>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="nh-state nh-error">
      <span>No se pudo cargar el historial.</span>
      <button class="nh-retry" @click="fetchHistory">Reintentar</button>
    </div>

    <!-- Empty -->
    <div v-else-if="!events.length" class="nh-state">
      <span>No hay cambios registrados para esta nota todavía.</span>
    </div>

    <!-- Timeline -->
    <div v-else class="nh-timeline">
      <div v-for="(event, idx) in events" :key="event.batch || idx" class="nh-event">
        <div class="nh-event-header">
          <span class="nh-badge" :class="'nh-badge-' + event.event_type">{{ eventLabel(event) }}</span>
          <span class="nh-date">{{ formatDateTime(event.timestamp) }}</span>
          <span class="nh-user">por <strong>{{ event.user || 'Sistema' }}</strong></span>
        </div>

        <!-- Free-form descriptions (assignations, images) -->
        <ul v-if="event.descriptions && event.descriptions.length" class="nh-descriptions">
          <li v-for="(desc, dIdx) in event.descriptions" :key="dIdx">{{ desc }}</li>
        </ul>

        <!-- Field changes table -->
        <table v-if="event.changes && event.changes.length" class="nh-table">
          <thead>
            <tr>
              <th>Campo</th>
              <th>Valor anterior</th>
              <th>Valor nuevo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(change, cIdx) in event.changes" :key="cIdx">
              <td class="nh-field">{{ change.label }}</td>
              <td class="nh-old"><span v-if="change.old">{{ change.old }}</span><em v-else>—</em></td>
              <td class="nh-new"><span v-if="change.new">{{ change.new }}</span><em v-else>—</em></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'NoteHistory',
  props: {
    noteIdent: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      error: false,
      events: [],
    };
  },
  mounted() {
    // Component only mounts when the "Historial" tab is opened (v-if),
    // so this lazy-loads the history and does not affect note load times.
    this.fetchHistory();
  },
  methods: {
    async fetchHistory() {
      if (!this.noteIdent) return;
      this.loading = true;
      this.error = false;
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        const response = await axios.get(
          `${apiDirectory}/notes/${this.noteIdent}/history/`
        );
        this.events = (response.data && response.data.events) || [];
      } catch (e) {
        this.error = true;
        this.events = [];
      } finally {
        this.loading = false;
      }
    },
    eventLabel(event) {
      const map = {
        field: 'Edición',
        status: 'Cambio de estado',
        invoice: 'Facturación',
        assignation: 'Asignación',
        image: 'Imágenes',
      };
      return map[event.event_type] || 'Cambio';
    },
    formatDateTime(value) {
      if (!value) return '';
      const date = new Date(value);
      if (isNaN(date.getTime())) return String(value);
      return date.toLocaleString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.note-history {
  padding: 16px 20px;
  font-family: 'Poppins', sans-serif;
}
.note-history .title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.note-history .title h4 {
  margin: 0;
}
.nh-refresh,
.nh-retry {
  border: 1px solid #d0d0d0;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  padding: 4px 10px;
  font-size: 14px;
}
.nh-refresh:disabled {
  opacity: 0.5;
  cursor: default;
}
.nh-state {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  color: #666;
}
.nh-error {
  color: #b00020;
}
.nh-spinner {
  width: 18px;
  height: 18px;
  border: 3px solid #e0e0e0;
  border-top-color: #888;
  border-radius: 50%;
  animation: nh-spin 0.8s linear infinite;
}
@keyframes nh-spin {
  to { transform: rotate(360deg); }
}
.nh-timeline {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.nh-event {
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fafafa;
}
.nh-event-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.nh-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 999px;
  color: #fff;
  background: #607d8b;
}
.nh-badge-field { background: #1976d2; }
.nh-badge-status { background: #f57c00; }
.nh-badge-invoice { background: #6a1b9a; }
.nh-badge-assignation { background: #2e7d32; }
.nh-badge-image { background: #00838f; }
.nh-date {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}
.nh-user {
  font-size: 13px;
  color: #666;
}
.nh-descriptions {
  margin: 0 0 8px 0;
  padding-left: 18px;
  font-size: 14px;
  color: #333;
}
.nh-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}
.nh-table th,
.nh-table td {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}
.nh-table th {
  background: #f2f4f7;
  font-weight: 600;
  color: #444;
}
.nh-table tr:last-child td {
  border-bottom: none;
}
.nh-field {
  font-weight: 600;
  color: #333;
  width: 28%;
}
.nh-old {
  color: #b00020;
  width: 36%;
  word-break: break-word;
}
.nh-new {
  color: #1b7f3b;
  width: 36%;
  word-break: break-word;
}
.nh-old em,
.nh-new em {
  color: #aaa;
}
</style>
